import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

export interface DropdownMenuContextValue {
  /** Whether the dropdown is open */
  open: boolean;
  /** Function to set open state */
  setOpen: (open: boolean) => void;
  /** Reference to the trigger element for positioning */
  triggerRef: React.RefObject<HTMLElement>;
  /** Close the dropdown */
  close: () => void;
}

const DropdownMenuContext = createContext<DropdownMenuContextValue | null>(
  null,
);

export function useDropdownMenu(): DropdownMenuContextValue {
  const context = useContext(DropdownMenuContext);
  if (!context) {
    throw new Error(
      "useDropdownMenu must be used within a DropdownMenuProvider",
    );
  }
  return context;
}

interface DropdownMenuProviderProps {
  children: React.ReactNode;
  /** Controlled open state */
  open?: boolean;
  /** Callback when open state changes */
  onOpenChange?: (open: boolean) => void;
  /** Default open state (uncontrolled) */
  defaultOpen?: boolean;
  /** Whether the dropdown is modal (closes on outside interaction) */
  modal?: boolean;
}

/**
 * DropdownMenuProvider - Root component that manages dropdown state
 */
export function DropdownMenuProvider({
  children,
  open: openProp,
  onOpenChange,
  defaultOpen = false,
  modal = true,
}: DropdownMenuProviderProps) {
  const [_open, _setOpen] = useState(defaultOpen);
  const triggerRef = useRef<HTMLElement>(null);

  // Use controlled state if provided, otherwise use internal state
  const open = openProp ?? _open;

  const setOpen = useCallback(
    (value: boolean) => {
      if (onOpenChange) {
        onOpenChange(value);
      } else {
        _setOpen(value);
      }
    },
    [onOpenChange],
  );

  const close = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  // Handle Escape key to close
  useEffect(() => {
    if (!open || !modal) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        close();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, modal, close]);

  const value: DropdownMenuContextValue = {
    open,
    setOpen,
    triggerRef,
    close,
  };

  return (
    <DropdownMenuContext value={value}>
      {children}
    </DropdownMenuContext>
  );
}
